// src/features/insight-v2-shared/components/PropertySelector.tsx
//
// The single-property picker of every v2 screen (Operación, the live board,
// reconciliation, control, status, benchmark, fit): each v2 read endpoint is
// scoped to exactly one v2 `property` per request. Fed by
// `useV2PropertySelection`, never by the v1 hotel list. With nothing to choose
// from (no access, or the list failed to load) it renders nothing — the page
// body says why, through `PropertyState`.
import React from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { ApartmentRounded as PropertyIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { V2PropertySelection } from '@/hooks/useV2PropertySelection';

interface PropertySelectorProps {
  selection: V2PropertySelection;
}

export const PropertySelector: React.FC<PropertySelectorProps> = ({ selection }) => {
  const translate = useTranslate();
  const { status, properties, propertyId, setPropertyId } = selection;
  if (status === 'empty' || status === 'error') return null;

  const label = translate('resources.insightV2.property.label');
  const adornment = <PropertyIcon fontSize="small" sx={{ ml: 0.5, mr: -0.25, color: 'text.secondary' }} />;
  const width = { xs: '100%', sm: 260 };

  if (status === 'loading') {
    return (
      <TextField
        size="small"
        disabled
        label={label}
        placeholder={translate('resources.insightV2.property.placeholder')}
        sx={{ width }}
        InputProps={{ startAdornment: adornment, endAdornment: <CircularProgress size={16} /> }}
      />
    );
  }

  // 'ready' guarantees a non-empty list and a propertyId taken from it.
  const selected = properties.find((p) => p.id === propertyId) ?? properties[0];

  return (
    <Autocomplete
      size="small"
      sx={{ width }}
      options={properties}
      disableClearable
      value={selected}
      getOptionLabel={(p) => p.displayName}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, option) => setPropertyId(option.id)}
      renderInput={(params) => (
        <TextField {...params} label={label} InputProps={{ ...params.InputProps, startAdornment: adornment }} />
      )}
    />
  );
};

export default PropertySelector;
